<template>
  <nav class="navigation">
    <div class="nav-container">
      <div class="nav-left">
          <router-link to="/" class="nav-logo">
              <img src="/img/logo_quare.svg" alt="Invoice Scan" class="logo-text" style="height: 32px" />
          </router-link>

          <!-- The sections of the app, beside the logo. The right-hand group
               is kept for the account: profile, subscription, signing out. -->
          <div class="nav-sections">
            <router-link to="/" class="btn-icon" :data-label="$t('nav.dashboard')" :aria-label="$t('nav.dashboard')">
              <LineIcon name="file-text" />
            </router-link>
            <router-link to="/companies" class="btn-icon" :data-label="$t('nav.companies')" :aria-label="$t('nav.companies')">
              <LineIcon name="building" />
            </router-link>
            <router-link to="/exchange-rates" class="btn-icon" :data-label="$t('exchangeRates.navLabel')" :aria-label="$t('exchangeRates.navLabel')">
              <LineIcon name="coins" />
            </router-link>
          </div>
      </div>

      <!-- Empty, but kept: flex: 1 makes it the spacer that holds nav-right
           against the right edge. -->
      <div class="nav-center"></div>

      <div class="nav-right">
        <div class="user-info">
          <span class="user-name">{{ userName }}</span>
        </div>
        <router-link to="/profile" class="btn-icon" :data-label="$t('nav.profile')" :aria-label="$t('nav.profile')">
          <LineIcon name="user" />
        </router-link>
        <router-link to="/subscription" class="btn-icon" :data-label="$t('profile.subscription')" :aria-label="$t('profile.subscription')">
          <LineIcon name="credit-card" />
        </router-link>
        <button @click="handleLogout" class="btn-icon" :data-label="$t('auth.logout')" :aria-label="$t('auth.logout')">
          <LineIcon name="log-out" />
        </button>
        <LanguageSelector />
      </div>

      <!-- Hamburger Menu Button (Mobile Only) -->
      <button @click="toggleMenu" class="hamburger-btn">
        <LineIcon :name="menuOpen ? 'x' : 'menu'" :size="20" :stroke-width="1.6" />
      </button>
    </div>

    <!-- Mobile Menu Overlay -->
    <div v-if="menuOpen" class="mobile-menu-overlay" @click="closeMenu"></div>
    
    <!-- Mobile Menu -->
    <div class="mobile-menu" :class="{ open: menuOpen }">
      <div class="mobile-menu-header">
        <div class="user-info-mobile">
          <span class="user-name">{{ userName }}</span>
        </div>
      </div>
      
      <div class="mobile-menu-items">
        <!-- The three sections — dashboard, companies, rates — are not here:
             their icons are in the bar above, visible on a phone too, and a
             menu that repeats what is already on screen is just a longer
             menu. What IS here is what the bar hides on a phone: the
             account's own entries. -->
        <router-link to="/profile" class="mobile-menu-item" @click="closeMenu">
          <LineIcon name="user" />
          <span>{{ $t('nav.profile') }}</span>
        </router-link>
        
        <router-link to="/subscription" class="mobile-menu-item" @click="closeMenu">
          <LineIcon name="credit-card" />
          <span>{{ $t('profile.subscription') }}</span>
        </router-link>
        
        <!-- The language, as a row like the others rather than a lone box in
             the corner: a label on the left, the picker at the end of it. -->
        <!-- The code itself is this row's icon, so it sits where the other
             rows' glyphs sit: at the left, before the label. -->
        <div class="mobile-menu-language">
          <LanguageSelector />
          <span>{{ $t('profile.language') }}</span>
        </div>

        <button @click="handleLogout" class="mobile-menu-item logout-btn">
          <LineIcon name="log-out" />
          <span>{{ $t('auth.logout') }}</span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import api from '@/utils/api';
import LanguageSelector from '@/components/LanguageSelector.vue';
import LineIcon from '@/components/icons/LineIcon.vue';

const router = useRouter();
const authStore = useAuthStore();

const userName = computed(() => authStore.user?.name || 'User');
const menuOpen = ref(false);

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const handleLogout = async () => {
  closeMenu();
  try {
    await authStore.logout();
  } catch (error) {
    console.error('Logout failed:', error);
  }
};
</script>

<style scoped>
.navigation {
  background: rgba(244, 243, 248, 0.8);
  -webkit-backdrop-filter: saturate(160%) blur(16px);
  backdrop-filter: saturate(160%) blur(16px);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: none;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em;
}
.navigation a, .navigation button, .navigation select { font-family: inherit; }

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.nav-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.nav-logo:hover {
  opacity: 0.8;
}

.logo-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.logo-text {
  height: 20px;
  flex-shrink: 0;
}

.nav-sections {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--gray);
  text-decoration: none;
  transition: all 0.2s ease;
}

.nav-link:hover {
  background: var(--light-gray);
  color: var(--dark);
}

.nav-link {
  border-radius: 11px;
}
.nav-link.active {
  background: rgba(43, 130, 240, 0.1);
  color: #2b82f0;
  box-shadow: none;
}

.nav-link svg {
  display: none;
}

.nav-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info {
  padding: 9px 8px;
  background: transparent;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #2b82f0;
}

.btn-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 11px;
  color: var(--gray);
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

/* No box behind the icon: the hover is the icon turning blue, lifting two
   pixels, and naming itself underneath. */
.btn-icon:hover {
  color: #2b82f0;
  transform: translateY(-2px);
}

/* Our own tooltip in place of the browser's: the OS draws that one in its own
   grey box and no stylesheet can reach it. Same shape — a small box under the
   icon — but no border and the text in the bar's blue.
   
   It hangs below the bar rather than inside it. There are only 12px of bar
   under a 40px icon, and the nav's own z-index keeps the box above whatever
   it covers. */
.btn-icon::after {
  content: attr(data-label);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 10px;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 10px 24px -10px rgba(31, 41, 51, 0.45);
  color: #2b82f0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.btn-icon:hover::after {
  opacity: 1;
}

/* Signing out names itself in its own colour. */
button.btn-icon::after {
  color: #e04545;
}

.btn-icon svg {
  flex-shrink: 0;
}

/* Signing out keeps its warning colour; the gesture is the same. */
button.btn-icon:hover {
  color: #e04545;
}

/* Hamburger Button
 *
 * Just the three lines: no box, no border, drawn at the same hairline weight
 * as the icons beside it. It was a bordered white square — a button around a
 * mark that is already a button. */
.hamburger-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s ease;
  color: var(--dark);
}

.hamburger-btn:hover {
  color: #2b82f0;
}

/* Mobile Menu Overlay */
.mobile-menu-overlay {
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  /* Use an explicit viewport-based height instead of top+bottom: the nav is the
     containing block for this fixed element, so bottom:0 collapsed it to ~0px and
     nothing got dimmed. vh is relative to the viewport regardless. */
  height: calc(100vh - 60px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: fixed;
  top: 60px;
  right: 0;
  width: 256px;
  max-width: 85vw;
  height: calc(100vh - 60px);
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  /* Hide off-screen by the panel's OWN width, so it is reliably hidden even when a
     transformed ancestor changes the containing block (right: -100% was not).
     Also visibility:hidden so a scrollbar gutter can never leave an edge peeking. */
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.3s ease, visibility 0s linear 0.3s;
  z-index: 999;
  overflow-y: auto;
}

.mobile-menu.open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.3s ease, visibility 0s linear 0s;
}

/* The drawer, in the same dress as the rest of the app.
 *
 * It was a blue gradient slab with white text over a list of 16px rows — a
 * panel from a different product. Everything here is white with a hairline
 * under the name, the rows are the quiet kind the dashboard uses, and the
 * one in colour is the one that matters: the way out. */
.mobile-menu-header {
  padding: 14px 16px;
  border-bottom: 1px solid #dde3ea;
  background: white;
}

.user-info-mobile {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary);
}

.user-info-mobile .user-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--primary);
}

.mobile-menu-items {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.mobile-menu-item:hover {
  background: var(--primary-light);
  color: var(--primary);
}

.mobile-menu-item svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  color: inherit;
}

.mobile-menu-item.router-link-active {
  background: var(--primary-light);
  color: var(--primary);
}

/* The language row: built like a menu item, with the picker sitting at its
   right end instead of a chevron. */
.mobile-menu-language {
  display: flex;
  align-items: center;
  /* The gap is what lands the label on the same 39px as the rows above:
     12px of padding, the 24px code box, and this. */
  gap: 3px;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.mobile-menu-language svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.mobile-menu-language > span {
  flex: 1;
}

/* The picker is drawn for the top bar, where it is a 40px icon square with
   the code centred in it. Here the code IS the row's icon, so the square and
   its padding go — otherwise the box starts 12px further in than the glyphs
   above it and drags the label with it. */
.mobile-menu-language :deep(.language-selector) {
  /* It carries 8px on its left for the gap in the top bar, where it follows
     the other icons. Here it is first in the row, and that 8px pushed both
     the code and the label out of the icon column. */
  margin: 0;
}

.mobile-menu-language :deep(.lang-btn) {
  width: 24px;
  height: 24px;
  padding: 0;
}

/* Signing out keeps its own red, and a line above it: it is not one more
   place to go. */
.logout-btn {
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid #dde3ea;
  border-radius: 0 0 8px 8px;
  color: #e04545;
}

.logout-btn:hover {
  background: #fff1f1;
  color: #e04545;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 12px;
    height: 60px;
  }

  .nav-left {
    flex: 0 0 auto;
    gap: 16px;
  }

  .logo-icon {
    width: 28px;
    height: 28px;
  }

  .logo-text {
    height: 18px;
  }

  .nav-logo {
    font-size: 16px;
  }

  .nav-center {
    flex: 1;
    justify-content: flex-start;
    gap: 4px;
    margin-left: 12px;
  }

  .nav-link {
    padding: 8px 12px;
    font-size: 13px;
  }

  .nav-right {
    display: none;
  }

  .hamburger-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 33px !important;
    height: 33px !important;
    padding: 0 !important;
    font-size: 16px !important;
  }

  .mobile-menu-overlay {
    display: block;
  }

  .mobile-menu {
    display: block;
  }

  .btn-icon {
    width: 36px;
    height: 36px;
  }

  .btn-icon svg {
    width: 18px;
    height: 18px;
  }
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
